import clsx from "clsx";
import { FC, ReactNode, useLayoutEffect, useRef } from "react";
import { Icons, useTranslation } from "rujira.ui";
import { useCardCollapse } from "../../../common/components/MobileCard";

export type OrderRowDetail = {
  label?: ReactNode;
  value: ReactNode;
  full?: boolean;
};

// Sizes every row's value column to the widest one, so the range pips all end
// at the same x. Each head is its own grid, so CSS can't line them up alone.
export const useAlignedValues = (rows: unknown[]) => {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const list = ref.current;
    if (!list) return;
    const lines = [
      ...list.querySelectorAll<HTMLElement>(".order-row__value-line"),
    ];

    let last = list.style.getPropertyValue("--order-row-values");
    const measure = () => {
      const widest = Math.max(0, ...lines.map((el) => el.offsetWidth));
      const next = `${widest}px`;
      if (next === last) return;
      last = next;
      list.style.setProperty("--order-row-values", next);
    };
    measure();

    const observer = new ResizeObserver(measure);
    lines.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [rows]);

  return ref;
};

// Drops every row's percentage fill under its title as soon as one row can't fit it beside the title.
export const useStackedBelow = (rows: unknown) => {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const list = ref.current;
    if (!list) return;

    const px = (value: string) => parseFloat(value) || 0;
    const measure = () => {
      const mains = [
        ...list.querySelectorAll<HTMLElement>(".order-row__main"),
      ].filter((main) =>
        main.querySelector(":scope > .order-row__aside--fill")
      );

      const stacked = mains.some((main) => {
        const title = main.querySelector<HTMLElement>(".order-row__title");
        const aside = main.querySelector<HTMLElement>(
          ".order-row__aside--fill"
        );
        if (!title || !aside) return false;
        const range = document.createRange();
        range.selectNodeContents(title);
        const titleWidth = Math.max(
          range.getBoundingClientRect().width,
          px(getComputedStyle(title).minWidth)
        );
        const needed =
          titleWidth +
          px(getComputedStyle(main).columnGap) +
          px(getComputedStyle(aside).minWidth);
        return needed > main.getBoundingClientRect().width;
      });

      list
        .querySelectorAll(".order-rows")
        .forEach((el) => el.classList.toggle("order-rows--stacked", stacked));
    };
    measure();

    // The list resizes when suspended rows mount and when the viewport changes
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    document.fonts.ready.then(measure);
    return () => observer.disconnect();
  }, [rows]);

  return ref;
};

export const OrderRows: FC<{ children: ReactNode; className?: string }> = ({
  children,
  className,
}) => <ul className={clsx("order-rows", className)}>{children}</ul>;

export const OrderRowsFallback: FC<{ count?: number; className?: string }> = ({
  count = 4,
  className,
}) => (
  <ul className={clsx("order-rows", className)}>
    {[...Array(count)].map((_, i) => (
      <li key={`order-row-skeleton-${i}`} className="order-row">
        <div className="order-row__head">
          <div className="lp order-row__icons">
            <div className="icon-denom skeleton br-2" />
            <div className="icon-denom skeleton br-2" />
          </div>
          <div className="order-row__main">
            <div className="skeleton h-2 w-10 br-2" />
            <div className="skeleton h-1.5 w-6 br-2" />
          </div>
          <div className="order-row__values">
            <div className="skeleton h-2 w-8 br-2" />
            <div className="skeleton h-1.5 w-5 br-2" />
          </div>
          <div className="skeleton w-2 h-2 br-2" />
        </div>
      </li>
    ))}
  </ul>
);

export const OrderRow: FC<{
  icons?: ReactNode;
  title: ReactNode;
  // Plain-text title for the expand button, so each row announces distinctly
  label: string;
  aside?: ReactNode;
  fillAside?: boolean;
  tags?: ReactNode;
  meta?: ReactNode;
  primary?: ReactNode;
  secondary?: ReactNode;
  headAction?: ReactNode;
  details?: OrderRowDetail[];
  columns?: 2 | 3;
  actions?: ReactNode;
  actionsLastColumn?: boolean;
  actionsFullRow?: boolean;
  // Custom expanded content, rendered above the default details card
  children?: ReactNode;
}> = ({
  icons,
  title,
  label,
  aside,
  fillAside,
  tags,
  meta,
  primary,
  secondary,
  headAction,
  details = [],
  columns = 2,
  actions,
  actionsLastColumn,
  actionsFullRow,
  children,
}) => {
  // Pinned: the algorithmic leaderboard renders rows outside the trade namespace
  const { t } = useTranslation("trade");
  const { collapsed, toggle } = useCardCollapse();

  const action = headAction && (
    <span className="order-row__head-action">{headAction}</span>
  );

  return (
    <li className={clsx("order-row", { "order-row--open": !collapsed })}>
      <div className="order-row__head">
        <button
          type="button"
          className="order-row__toggle pointer"
          aria-expanded={!collapsed}
          aria-label={t("orderRowDetails", { label })}
          onClick={toggle}
        />
        <span className="lp order-row__icons">{icons}</span>
        <span className="order-row__main">
          <span className="order-row__title">{title}</span>
          {aside && (
            <span
              className={clsx("order-row__aside", {
                "order-row__aside--fill": fillAside,
              })}>
              {aside}
            </span>
          )}
        </span>
        <span className="order-row__values">
          {primary && (
            <span className="order-row__value-line">
              <span className="order-row__primary">{primary}</span>
              {action}
            </span>
          )}
          {(secondary || (!primary && action)) && (
            <span className="order-row__value-line">
              {secondary && (
                <span className="order-row__secondary">{secondary}</span>
              )}
              {!primary && action}
            </span>
          )}
        </span>
        <Icons.AngleDown className="order-row__chevron w-2 h-2 color-grey" />
      </div>
      <div className="order-row__reveal">
        <div className="order-row__body">
          {children}
          {(tags || meta || details.length > 0 || actions) && (
            <div className="order-row__card">
              {(tags || meta) && (
                <div
                  className={clsx("order-row__card-head", {
                    "order-row__card-head--3": columns === 3,
                  })}>
                  {tags && <div className="order-row__tags">{tags}</div>}
                  {meta && <div className="order-row__meta">{meta}</div>}
                </div>
              )}
              {(details.length > 0 || actions) && (
                <div
                  className={clsx("order-row__details", {
                    "order-row__details--3": columns === 3,
                  })}>
                  {details.map(({ label, value, full }, i) => (
                    <div
                      key={i}
                      className={clsx("order-row__detail", {
                        "order-row__detail--full": full,
                      })}>
                      {label && (
                        <span className="mobile-card__label">{label}</span>
                      )}
                      <span className="order-row__value">{value}</span>
                    </div>
                  ))}
                  {actions && (
                    <div
                      className={clsx("order-row__actions", {
                        "order-row__actions--last-column": actionsLastColumn,
                        "order-row__actions--full-row": actionsFullRow,
                      })}>
                      {actions}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </li>
  );
};
